// 1. React and core hooks
import { useEffect, useRef, useState } from 'react';

// 2. External libraries
import { useClerk } from '@clerk/clerk-react';
import { getRouteApi, useNavigate } from '@tanstack/react-router';

// 3. Internal components
import { LoadingSpinner } from '~/components/common/LoadingSpinner';
import { MagicLinkForm } from '~/components/auth/MagicLinkForm';
import { CenteredAuthLayout } from '~/components/common/CenteredAuthLayout';
import { ErrorAlert } from '~/components/common/ErrorAlert';
import { Alert, AlertTitle } from '~/components/ui/alert';
import { Button } from '~/components/ui/button';

// 4. Type imports
import type { AttributionData } from '~/types/onboarding';

// 5. Custom hooks and utilities (§TS.4.2 - alphabetized)
import { RETURN_URL_KEY } from '~/components/auth/RedirectToSignInWithReturn';
import { ATTRIBUTION_CALLBACK_TIMEOUT_MS } from '~/utils/attributionConstants';
import { extractClerkErrorCode } from '~/utils/clerkErrorHandler';
import { useAuth } from '~/hooks/useAuth';
import { useAttributionStorage } from '~/hooks/useAttribution';
import { useUserMetadataMutation } from '~/hooks/useUserMetadataMutation';
import { logWarn } from '~/utils/logger';

type VerificationState = 'verifying' | 'expired' | 'success' | 'error';

// Routes that should bypass onboarding redirect after login
const ONBOARDING_BYPASS_ROUTES = ['/telegram/connect'];

// Clerk error codes that indicate the magic link is no longer usable (expired,
// already consumed, or the ticket/token was invalid). Branching on these codes
// is more reliable than substring-matching the error message.
const EXPIRED_LINK_ERROR_CODES = new Set([
  'verification_expired',
  'verification_failed',
  'verification_already_verified',
  'client_state_stale',
  'form_code_incorrect',
]);

const routeApi = getRouteApi('/magic-link-callback');

export function MagicLinkCallbackPage() {
  const { handleEmailLinkVerification } = useClerk();
  const { user, isLoaded } = useAuth();
  const { getAttribution, clearAttribution } = useAttributionStorage();
  const navigate = useNavigate();
  const { redirect_url: redirectUrl } = routeApi.useSearch();
  const metadataMutation = useUserMetadataMutation();
  const [verificationState, setVerificationState] = useState<VerificationState>('verifying');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Get the redirect URL from query params OR sessionStorage (set by RedirectToSignInWithReturn)
  // sessionStorage is more reliable as query params can be lost during auth flows
  // IMPORTANT: Use ref to capture redirect URL on MOUNT only - ensures value is stable across all renders (§TS.5.2)
  const redirectUrlRef = useRef<string | null>(
    redirectUrl || sessionStorage.getItem(RETURN_URL_KEY)
  );

  // Prevent multiple executions of the navigation flow
  const isProcessingRef = useRef(false);

  useEffect(() => {
    async function verifyEmailLink(): Promise<void> {
      try {
        // Verify the magic link from email
        await handleEmailLinkVerification({
          redirectUrl: `${window.location.origin}/magic-link-callback`,
          redirectUrlComplete: `${window.location.origin}/magic-link-callback`,
        });

        // Verification succeeded - Clerk handles session activation internally
        // If no error was thrown, the verification was successful
        setVerificationState('success');
      } catch (error) {
        console.error('Failed to verify magic link:', error);

        // Check for restriction errors first
        const clerkErrorResult = extractClerkErrorCode(error);
        if (clerkErrorResult.shouldRedirect) {
          void navigate({
            to: '/auth-error',
            search: { type: clerkErrorResult.scenario },
            replace: true,
          });
          return;
        }

        // Check if error indicates an expired/invalid link via Clerk's error code
        // rather than fragile substring matching on the error message.
        const isExpired = EXPIRED_LINK_ERROR_CODES.has(clerkErrorResult.code ?? '');

        if (isExpired) {
          setVerificationState('expired');
        } else {
          setVerificationState('error');
          setErrorMessage('Failed to verify magic link. Please try again.');
        }
      }
    }

    void verifyEmailLink();
  }, [handleEmailLinkVerification, navigate]);

  useEffect(() => {
    async function writeAttributionAndNavigate(): Promise<void> {
      if (!isLoaded || !user || verificationState !== 'success') {
        return;
      }

      // Prevent concurrent executions
      if (isProcessingRef.current) {
        return;
      }
      isProcessingRef.current = true;

      // Check redirect URL FIRST before any async operations
      const redirectUrl = redirectUrlRef.current;

      // Check for bypass routes FIRST - skip attribution write for faster redirect
      if (redirectUrl) {
        try {
          const url = new URL(redirectUrl, window.location.origin);
          const pathname = url.pathname;
          const shouldBypassOnboarding = ONBOARDING_BYPASS_ROUTES.some((route) =>
            pathname.startsWith(route)
          );

          if (shouldBypassOnboarding) {
            // Clear the stored return URL
            sessionStorage.removeItem(RETURN_URL_KEY);
            const targetUrl = url.pathname + url.search;
            // Use window.location for hard navigation - the router navigate() gets overridden by Clerk
            window.location.href = targetUrl;
            return;
          }
        } catch {
          // Invalid URL, fall through to default behavior
        }
      }

      // If not bypassing, proceed with attribution write
      // Read attribution from sessionStorage
      const attribution = getAttribution();

      // Write attribution to Clerk publicMetadata via backend API with timeout and retry
      let writeSuccess = false;
      const maxAttempts = 2;

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          const updatePromise = metadataMutation.mutateAsync({
            attribution:
              attribution ||
              ({
                referrer: null,
                utmSource: null,
                utmMedium: null,
                utmCampaign: null,
                utmContent: null,
                utmTerm: null,
              } satisfies AttributionData),
          });

          // Wait for update with timeout (§BASIC.6 - avoid unnecessary abstraction)
          await Promise.race([
            updatePromise,
            new Promise((_, reject) =>
              setTimeout(
                () => reject(new Error('Attribution write timeout')),
                ATTRIBUTION_CALLBACK_TIMEOUT_MS
              )
            ),
          ]);

          writeSuccess = true;
          break;
        } catch (error) {
          logWarn(`Attribution write attempt ${attempt}/${maxAttempts} failed:`, error);

          if (attempt === maxAttempts) {
            logWarn('Failed to write attribution after 2 attempts, proceeding with navigation');
          }
        }
      }

      // Clear attribution from sessionStorage after write attempt
      if (writeSuccess) {
        clearAttribution();
      }

      // Check onboarding status and navigate
      const onboardingComplete = user.publicMetadata?.onboardingComplete === true;

      // Clear the stored return URL before navigating
      sessionStorage.removeItem(RETURN_URL_KEY);

      if (onboardingComplete) {
        void navigate({ to: '/', replace: true });
      } else {
        void navigate({ to: '/onboarding', replace: true });
      }
    }

    void writeAttributionAndNavigate();
    // Note: metadataMutation excluded from deps to prevent infinite loop
    // Note: redirectUrlRef excluded from deps - it's a ref that captures value on mount (§TS.5.2)
    // oxlint-disable-next-line react/exhaustive-deps
  }, [isLoaded, user, verificationState, getAttribution, clearAttribution, navigate]);

  const handleBackToSignIn = () => {
    void navigate({ to: '/sign-in', replace: true });
  };

  // Loading state during verification
  if (verificationState === 'verifying') {
    return (
      <CenteredAuthLayout>
        <LoadingSpinner message="Verifying magic link..." />
      </CenteredAuthLayout>
    );
  }

  // Expired link state - show resend option
  if (verificationState === 'expired') {
    return (
      <CenteredAuthLayout contentWidth="narrow">
        <Alert variant="warning" className="mb-6">
          <div>
            <AlertTitle>Magic Link Expired</AlertTitle>
            Magic links expire after 15 minutes. Please request a new link.
          </div>
        </Alert>
        <MagicLinkForm onBackToOAuth={handleBackToSignIn} redirectUrl={redirectUrl} />
      </CenteredAuthLayout>
    );
  }

  // Error state - show error message with retry option (§TS.8.1 - error states)
  if (verificationState === 'error') {
    return (
      <CenteredAuthLayout contentWidth="narrow">
        <ErrorAlert className="mb-6" title="Verification Failed" message={errorMessage} />
        <Button size="lg" className="w-full" onClick={handleBackToSignIn}>
          Back to Sign In
        </Button>
      </CenteredAuthLayout>
    );
  }

  // Success state - loading while writing attribution and navigating
  return (
    <CenteredAuthLayout>
      <LoadingSpinner message="Completing sign-in..." />
    </CenteredAuthLayout>
  );
}
